/**
 * 品牌模式的权限来源：/auth/entities 的 clusters（2026-10-30 起，取代原来的 brands）。
 *
 * 一个 cluster = 一个集团下的一组品牌（+ 国家范围），带账号在里面能看的酒店：
 *   { name, hotelGroup, role, hotels: [酒店，同 /auth/entities 的 hotels 条目], brands: ["EDITION Hotels"] }
 * 品牌模式能不能进、左上角能切哪几个、品牌 / 集团报告（/cluster/brand、/cluster/group）用哪个
 * clusterName，都看它。store 里仍放在 auth.brands / auth.selectedBrandName（名字是历史叫法，
 * 存的是 cluster 和 cluster 名），所以侧栏、选择页这些按 .name / .hotelGroup 读的地方不用改。
 *
 * 只有这两个报告接口按 cluster 取；同一页里的 linked-cases、group-hotels、组合 PDF 导出等后端还是
 * 按品牌名（brandName + country）和品牌权限，品牌名用 primaryBrandOfCluster 取。
 */

// 品牌名比较：压成纯小写字母数字（"EDITION Hotels" / "Edition hotels" 算同一个）
const brandKey = (name) => String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

/**
 * /auth/entities 的 clusters 规整成品牌模式用的列表：去掉没有名字的，hotels / brands 补成数组。
 * rename：名字的显示改名（演示品牌，见 config/demoAccount.js 的 renameDemoBrand）
 */
export function normalizeClusters(list, rename = (name) => name) {
  return (Array.isArray(list) ? list : [])
    .filter((c) => c && c.name)
    .map((c) => ({
      ...c,
      name: rename(c.name),
      hotels: Array.isArray(c.hotels) ? c.hotels : [],
      brands: Array.isArray(c.brands) ? c.brands.filter(Boolean) : [],
    }));
}

/**
 * cluster 名的显示写法：下划线换成空格（"Marriott_China" → "Marriott China"）。
 * 只用于显示；接口参数、匹配、存储一律用原名
 */
export function clusterDisplayName(name) {
  return String(name || "").replace(/_+/g, " ").replace(/\s+/g, " ").trim();
}

/** 按名字找 cluster；找不到返回 null */
export function findCluster(clusters, name) {
  if (!name) return null;
  return (clusters || []).find((c) => c && c.name === name) || null;
}

/**
 * 这家酒店属于哪个 cluster：先看 cluster.hotels 里有没有它（按 hotelId），没有再按酒店的品牌
 * 对上 cluster.brands。都对不上返回 null（这家酒店的账号没有品牌 / 集团报告的权限）。
 */
export function clusterForHotel(clusters, hotel) {
  if (!hotel) return null;
  const list = clusters || [];
  const id = hotel.hotelId != null ? String(hotel.hotelId) : "";
  const byHotel = id
    ? list.find((c) => (c.hotels || []).some((h) => h && String(h.hotelId) === id))
    : null;
  if (byHotel) return byHotel;
  const key = brandKey(hotel.brand);
  if (!key) return null;
  return list.find((c) => (c.brands || []).some((b) => brandKey(b) === key)) || null;
}

/** 有这个品牌的第一个 cluster（品牌名不分大小写、空格），没有返回 null */
export function clusterForBrand(clusters, brandName) {
  const key = brandKey(brandName);
  if (!key) return null;
  return (clusters || []).find((c) => (c.brands || []).some((b) => brandKey(b) === key) || brandKey(c.name) === key) || null;
}

/** 调还按品牌名的老接口时用的品牌名：cluster.brands 的第一个，没有就用 cluster 名 */
export function primaryBrandOfCluster(cluster) {
  if (!cluster) return null;
  return (cluster.brands && cluster.brands[0]) || cluster.name || null;
}

/**
 * 进品牌模式时顺带选中的酒店：cluster 里、且在账号酒店列表 hotelList 里的第一家（按列表顺序）；
 * cluster 里一家都对不上时退回同集团（hotelGroup）的第一家；再没有返回 null。
 */
export function firstHotelOfCluster(cluster, hotelList) {
  if (!cluster) return null;
  const list = hotelList || [];
  const ids = new Set((cluster.hotels || []).map((h) => h && String(h.hotelId)));
  const inCluster = list.find((h) => ids.has(String(h.hotelId)));
  if (inCluster) return inCluster;
  return cluster.hotelGroup ? list.find((h) => h.hotelGroup === cluster.hotelGroup) || null : null;
}
